import { useEffect, useState } from 'react';

import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { Spinner } from 'cpool-components/state/Spinner';
import { AGGREGATE_NAMES, LOAN_REQUEST_OFFER_SOURCES, LOAN_REQUEST_RESOLVERS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import { CreateOfferButton } from './Header/CreateOfferButton';
import { SynchronizeOffersButton } from './Header/SynchronizeOffersButton';
import { useAcceptedOfferCounts } from './hooks/useAcceptedOfferCounts';
import { useAcceptedOffers } from './hooks/useAcceptedOffers';
import { OfferManagementFilter } from './OfferManagementFilter';
import { CreateOfferModal } from './OfferModal/CreateOfferModal';
import { EditOfferModal } from './OfferModal/EditOfferModal';
import { OfferInfoDialog } from './Table/Cells/OfferInfoDialog';
import { RouteCreditWorthinessDialog } from './Table/Cells/RouteCreditWorthinessDialog';
import { ConsorsNotesDialog } from './Table/ConsorsNotes';
import { OfferManagementTable } from './Table/OfferManagementTable';
import { isManualAcceptedOffer, type ManualAcceptedOffer } from './types';
import { LEAD_DETAILS_OVERLAYS } from '~/features/leadDetails/constants/overlays';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import useLead from '~/features/leadDetails/hooks/useLead';
import { useLeadDetailsModalNavigation } from '~/features/leadDetails/hooks/useLeadDetailsModalNavigation';
import { updateLeadDetailsOverlaySearch } from '~/features/leadDetails/utils/normalizeLeadDetailsSearch';

const leadDetailsRoute = getRouteApi('/_app/leads_/details/$leadId');

export const OfferManagement: React.FC = () => {
    const { t } = useTranslation();
    const { leadId } = leadDetailsRoute.useParams();
    const { data: lead } = useLead(leadId);
    const navigate = useNavigate({ from: '/leads/details/$leadId/offer-management' });
    const { creditWorthinessId, editOfferId, offerInfoId } = leadDetailsRoute.useSearch();
    const { closeModal, isModalOpen, openModal } = useLeadDetailsModalNavigation();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const isConsorsNotesOpen = isModalOpen(LEAD_DETAILS_OVERLAYS.CONSORS_NOTES);
    const isCreateOfferOpen = isModalOpen(LEAD_DETAILS_OVERLAYS.CREATE_OFFER);

    const {
        data: acceptedOffers,
        getCriteriaFilter,
        getStatusFilter,
        isFetching,
        isLoading,
        setCriteriaFilter,
        setStatusFilter,
    } = useAcceptedOffers(leadId);

    const { data: counts } = useAcceptedOfferCounts(leadId);
    const canOpenConsorsNotes =
        Boolean(lead?.consors?.subscriptionIdentifier) &&
        permissions.resolvers[AGGREGATE_NAMES.LOAN_REQUEST][LOAN_REQUEST_RESOLVERS.getConsorsNotesByLeadId];
    const offerInfo = acceptedOffers?.find((offer) => offer.id === offerInfoId);
    const editedManualOffer =
        acceptedOffers?.find(
            (offer): offer is ManualAcceptedOffer => offer.id === editOfferId && isManualAcceptedOffer(offer),
        ) ?? null;

    // Keeps the last opened offer around while the dialog animates closed - see EditOfferModal's `manualOffer`.
    const [lastOfferInfo, setLastOfferInfo] = useState<typeof offerInfo | null>(null);
    const [isOfferInfoOpen, setIsOfferInfoOpen] = useState(false);
    useEffect(() => {
        if (offerInfo) setLastOfferInfo(offerInfo);
    }, [offerInfo]);
    // Opens one render after mounting, so the Modal transition animates without an `appear` prop.
    useEffect(() => {
        setIsOfferInfoOpen(Boolean(offerInfo) && Boolean(lastOfferInfo));
    }, [offerInfo, lastOfferInfo]);

    const closeOfferOverlay = (
        key:
            | typeof LEAD_DETAILS_OVERLAYS.CREDIT_WORTHINESS_ID
            | typeof LEAD_DETAILS_OVERLAYS.EDIT_OFFER_ID
            | typeof LEAD_DETAILS_OVERLAYS.OFFER_INFO_ID,
    ) => {
        navigate({
            replace: true,
            search: (previous) => updateLeadDetailsOverlaySearch(previous, key, undefined),
        });
    };

    useEffect(() => {
        if (isConsorsNotesOpen && lead && !canOpenConsorsNotes) closeModal(LEAD_DETAILS_OVERLAYS.CONSORS_NOTES);
        if (!acceptedOffers || isFetching) return;

        const isOfferInfoInvalid = offerInfoId !== undefined && offerInfo === undefined;
        const isEditOfferInvalid = editOfferId !== undefined && editedManualOffer === null;
        const isCreditWorthinessInvalid =
            creditWorthinessId !== undefined &&
            !acceptedOffers.some(
                (offer) =>
                    offer.id === creditWorthinessId &&
                    (offer.source === LOAN_REQUEST_OFFER_SOURCES.EUROPACE ||
                        offer.source === LOAN_REQUEST_OFFER_SOURCES.EUROPACE_SYNC),
            );

        if (!isOfferInfoInvalid && !isCreditWorthinessInvalid && !isEditOfferInvalid) return;
        navigate({
            replace: true,
            search: (previous) => ({
                ...previous,
                [LEAD_DETAILS_OVERLAYS.CREDIT_WORTHINESS_ID]: isCreditWorthinessInvalid
                    ? undefined
                    : previous.creditWorthinessId,
                [LEAD_DETAILS_OVERLAYS.EDIT_OFFER_ID]: isEditOfferInvalid ? undefined : previous.editOfferId,
                [LEAD_DETAILS_OVERLAYS.OFFER_INFO_ID]: isOfferInfoInvalid ? undefined : previous.offerInfoId,
            }),
        });
    }, [
        acceptedOffers,
        canOpenConsorsNotes,
        closeModal,
        creditWorthinessId,
        editOfferId,
        editedManualOffer,
        isConsorsNotesOpen,
        isFetching,
        lead,
        navigate,
        offerInfo,
        offerInfoId,
    ]);

    return (
        <>
            <ConsorsNotesDialog
                isOpen={isConsorsNotesOpen && canOpenConsorsNotes}
                onClose={() => closeModal(LEAD_DETAILS_OVERLAYS.CONSORS_NOTES)}
            />
            {lastOfferInfo && (
                <OfferInfoDialog
                    isOpen={isOfferInfoOpen}
                    offer={lastOfferInfo}
                    onClose={() => closeOfferOverlay(LEAD_DETAILS_OVERLAYS.OFFER_INFO_ID)}
                />
            )}
            {creditWorthinessId && (
                <RouteCreditWorthinessDialog
                    leadId={leadId}
                    offerId={creditWorthinessId}
                    onClose={() => closeOfferOverlay(LEAD_DETAILS_OVERLAYS.CREDIT_WORTHINESS_ID)}
                />
            )}
            <CreateOfferModal open={isCreateOfferOpen} onClose={() => closeModal(LEAD_DETAILS_OVERLAYS.CREATE_OFFER)} />
            <EditOfferModal
                manualOffer={editedManualOffer}
                open={Boolean(editedManualOffer)}
                onClose={() => closeOfferOverlay(LEAD_DETAILS_OVERLAYS.EDIT_OFFER_ID)}
            />
            <div className="flex flex-wrap items-center justify-between gap-2 py-2">
                <h6 className="text-midnight">{t('OFFER_MANAGEMENT.TITLE')}</h6>
                <div className="flex items-center gap-2">
                    <SynchronizeOffersButton leadId={leadId} />
                    <CreateOfferButton onClick={() => openModal(LEAD_DETAILS_OVERLAYS.CREATE_OFFER)} />
                </div>
            </div>
            <OfferManagementFilter
                counts={counts ?? {}}
                getCriteriaFilter={getCriteriaFilter}
                getStatusFilter={getStatusFilter}
                setCriteriaFilter={setCriteriaFilter}
                setStatusFilter={setStatusFilter}
            />
            {isLoading ? <Spinner /> : <OfferManagementTable acceptedOffers={acceptedOffers} isFetching={isFetching} />}
        </>
    );
};
